// Add flags as: { CONSTANT_NAME: 'posthog-flag-key' }
// Usage: usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CONSTANT_NAME)
export const POSTHOG_FEATURE_FLAGS = {
  CP_SIDEBAR_GOTO_GRANT_FINDER_COPY_EXPERIMENT: 'cp_sidebar_goto_grant_finder_copy_experiment',
  CP_META_SMART_APPLY: 'cp_meta_smart_apply',
  CP_GOODSTACK_PRO_LOGIN_REDIRECT: 'cp_goodstack_pro_login_redirect',
  TIL_CAUSES_PORTAL_SITEWIDE_MESSAGE: 'til_causes_portal_sitewide_message',
  TIL_EXP_FAST_TRACK_GOOGLE_APPLICATION: 'til_exp_fast_track_google_application',
  CP_REQUEST_TO_JOIN_CAPTCHA: 'cp_request_to_join_captcha',
  CP_ADD_ORGANISATION_CAPTCHA: 'cp_add_organisation_captcha',
  CP_SIGN_UP_CAPTCHA: 'cp_sign_up_captcha',
  CP_BANK_DETAILS_VALIDATION: 'cp_bank_details_validation',
  BANK_DETAILS_VALIDATION_WITH_DOCUMENT: 'bank_details_validation_with_document',
  CP_DONATION_FORM_APPLICATION: 'cp_donation_form_application',
  CP_VOLUNTEERING_SECTION: 'cp_volunteering_section',
  CP_SMART_APPLY_DYNAMIC_LOGOS: 'cp_smart_apply_dynamic_logos',
  CP_SHOW_RETURNED_PAYOUTS: 'cp_show_returned_payouts',
  CP_GRANT_FINDER_BANNER_COPY: 'cp_grant_finder_banner_copy',
  // Multivariate copy experiment for the homepage grant finder banner. Carries the same
  // payload shape as CP_GRANT_FINDER_BANNER_COPY and takes precedence over it, so the
  // older flag must be switched off for the duration of the experiment.
  CP_GRANT_FINDER_BANNER_COPY_EXPERIMENT: 'cp_grant_finder_banner_copy_experiment',
  CP_GRANT_ADVISORY_CALENDLY: 'cp_grant_advisory_calendly',
  CP_GRANT_ADVISORY_QUESTIONNAIRE: 'cp_grant_advisory_questionnaire',
  CP_GRANT_ADVISORY_AFTER_SMART_APPLY: 'cp_grant_advisory_after_smart_apply',
  // Kill switch for every flow that submits to POST /v1/store/applications (Smart Apply,
  // Native Apply). Deliberately omitted from POSTHOG_FEATURE_FLAG_DEFAULTS so it
  // resolves to `false` while PostHog loads and when PostHog is unreachable — apply stays hidden
  // rather than offering a button that cannot succeed. Set to `true` in PostHog to restore.
  CP_PARTNER_APPLY_ENABLED: 'cp_partner_apply_enabled',
  CP_GRANT_ADVISORY_QUESTIONNAIRE_NON_BLOCKING: 'cp_grant_advisory_questionnaire_non_blocking',
  // Second Smart Apply modal for returning users who dismissed all 3 intro views without applying.
  // Omitted from POSTHOG_FEATURE_FLAG_DEFAULTS so it resolves to `false` while loading.
  CP_SECOND_SMART_APPLY: 'cp_second_smart_apply',
  CP_AB_TESTING_STATUS_CHECK_PAGE: 'cp_ab_testing_status_check_page',
  CP_ADOBE_EXPRESS_SECOND_SMART_APPLY: 'cp_adobe_express_second_smart_apply',
  CP_GFM_MODAL: 'cp_gfm_modal',
  CP_GFM_HOME_BANNER: 'cp_gfm_home_banner',
  CP_GFM_CAROUSEL_BANNER: 'cp_gfm_carousel_banner',
  CP_GFM_PRODUCT_TILE: 'cp_gfm_product_tile',
  CP_SIGNUP_PHONE_REQUIRED_US: 'cp_signup_phone_required_us'
} as const

export type PostHogFeatureFlag = typeof POSTHOG_FEATURE_FLAGS[keyof typeof POSTHOG_FEATURE_FLAGS]

/*
  Value used before PostHog has loaded flags or when PostHog is not configured
  (e.g. local dev without an API key). Flags omitted here default to `false`.
  Mirrors the production rollout so gated UI stays stable while flags resolve.
*/
export const POSTHOG_FEATURE_FLAG_DEFAULTS: Partial<Record<PostHogFeatureFlag, boolean>> = {
  [POSTHOG_FEATURE_FLAGS.TIL_EXP_FAST_TRACK_GOOGLE_APPLICATION]: true,
  [POSTHOG_FEATURE_FLAGS.CP_REQUEST_TO_JOIN_CAPTCHA]: true,
  [POSTHOG_FEATURE_FLAGS.CP_BANK_DETAILS_VALIDATION]: true,
  [POSTHOG_FEATURE_FLAGS.BANK_DETAILS_VALIDATION_WITH_DOCUMENT]: true
}

// JSON payload attached to the CP_GRANT_FINDER_BANNER_COPY and
// CP_GRANT_FINDER_BANNER_COPY_EXPERIMENT flags. Each field is optional and falls back to
// the i18n `grantFinderBanner.*` strings when unset, so the experiment's `control` variant
// can carry an empty payload and still render the current copy.
export type GrantFinderBannerCopyPayload = {
  header?: string // maps to the banner's header (the small line above the main text)
  description?: string // maps to the banner's description (the main, big text)
  cta?: string // the CTA button label
}

/*
  JSON payload attached to the CP_GRANT_ADVISORY_CALENDLY flag: per-entry-point
  HubSpot meetings URLs, editable in PostHog without a deploy. An entry point falls
  back to `default`. This payload is the ONLY source of these URLs - nothing is
  hardcoded in the app - so if it carries no URL for an entry point, that entry
  point does not offer the booking CTA at all.
*/
export type GrantAdvisoryBookingUrlPayload = {
  sidebar?: string
  smartApply?: string
  default?: string
}
